<template>
  <Teleport to="body">
    <div v-if="show" class="gate-overlay" role="dialog" aria-modal="true" aria-labelledby="gate-title">
      <div class="gate-modal">
        <div class="gate-icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>

        <h2 id="gate-title" class="gate-title">Out of tokens</h2>
        <p class="gate-body">
          Your token balance has reached zero. Top up to continue playing.
        </p>

        <form @submit.prevent="handleTopUp" class="gate-form">
          <div class="form-row">
            <label for="token-amount">Tokens to add</label>
            <input
              id="token-amount"
              v-model.number="amount"
              type="number"
              min="1"
              step="1"
              placeholder="e.g. 30"
              required
              :disabled="loading"
            />
          </div>

          <p class="gate-hint">
            Each active game costs 1 token per day.
          </p>

          <button type="submit" class="btn btn-primary gate-btn" :disabled="loading || !amount || amount < 1">
            {{ loading ? 'Adding…' : 'Add tokens' }}
          </button>

          <p v-if="error" class="gate-error">{{ error }}</p>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePlayerStore } from '@/stores/player'

const player = usePlayerStore()

const show = computed(() => player.profile !== null && player.profile.tokenBalance <= 0)

const amount = ref(30)
const loading = ref(false)
const error = ref(null)

async function handleTopUp() {
  if (!amount.value || amount.value < 1) return
  loading.value = true
  error.value = null
  try {
    await player.topUpTokens(amount.value)
    amount.value = 30
  } catch (e) {
    error.value = e.response?.data?.message ?? 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.gate-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.gate-modal {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  animation: modal-enter 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes modal-enter {
  from { opacity: 0; transform: scale(0.96) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.gate-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-warning-subtle);
  color: var(--color-warning);
  margin-bottom: 1.25rem;
}

.gate-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.gate-body {
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin-bottom: 1.75rem;
}

.gate-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: left;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-row label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.form-row input {
  width: 100%;
  box-sizing: border-box;
}

.gate-hint {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  margin: 0;
}

.gate-btn {
  width: 100%;
  padding: 0.65em 1em;
  margin-top: 0.25rem;
}

.gate-error {
  font-size: 0.875rem;
  color: var(--color-danger);
  margin: 0;
}
</style>
